import {
  dropTargetForElements,
  ElementDragPayload,
  monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/types';
import { colors } from '@atlaskit/theme';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useFlaggingCallback } from '@databases/flags';
import { ScrollPosition } from '@databases/layout/scroll-position';
import Z_INDEX from '@databases/layout/z-index';
import { usePrevious } from '@databases/react';
import { DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { addEntryToValueRange } from '@databases/redux/cellSelections/actions';
import {
  selectSelectedEntryIds,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { doAddEntry, doFillValues } from '@databases/redux/table-layout/actions';
import {
  HALF_ROW_SELECT_BUTTON_WIDTH,
  TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
} from '@databases/utils/constants';
import { DatabaseChangeType } from '@databases/utils/database/changesets';
import { areAllFieldsHidden } from '@databases/utils/database/view/view';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import { encodeCssUrl } from '@databases/utils/utils';
import useView from '@databases/views/useView';
import { sum } from 'lodash-es';
import svgToMiniDataURI from 'mini-svg-data-uri';
import React, { MutableRefObject, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  DraggingIem,
  DragOverItem,
  DragType,
  useDragItems,
  VerticalPositions,
} from '../../../drag-utils';
import {
  getSkeletonRowSvg,
  useAnimateItems,
  useRefMapWithSizes,
} from '../../../lazy-render';
import { useScope } from '../../Scope';
import { tableRowMinHeight } from '../../styled';

import TableLayoutEmptyStateRow from './TableLayoutEmptyStateRow';
import TableLayoutRow from './TableLayoutRow';

const DropIndicator = styled.tr`
  position: relative;
  :before {
    content: ' ';
    position: absolute;
    z-index: ${Z_INDEX.TABLE_VIEW_ROW_DROP_INDICATOR};
    left: 0;
    width: calc(100%);
    height: 4px;
    border-radius: 2px;
    background-color: ${colors.B200};
  }
`;

export const EMPTY_ITEM = 'empty';
export const ADD_ITEM = 'add';
export const DRAG_ITEM = 'drag';
const GAP_ITEM = 'gap';
export const CONTAINER_PADDING_TOP = 120;

/** The height to assume for rows that have not been rendered before. */
const FALLBACK_ROW_HEIGHT = tableRowMinHeight;

export interface TableLayoutRowsProps {
  addEntryButtonRef?: React.RefObject<HTMLButtonElement>;
  contentId: string;
  isContentEditable: boolean;
  canEditFields: boolean;
  canEditContent: boolean;
  editingIds?: MutableRefObject<Set<string>>;
  renderSection: ScrollPosition;
  currentWidths: Array<number | undefined>;
  containerTop: number;
  containerWidth: number;
}

const TableLayoutRows = React.memo(
  React.forwardRef<HTMLTableSectionElement, TableLayoutRowsProps>(
    function TableLayoutRows(
      {
        addEntryButtonRef,
        contentId,
        isContentEditable,
        canEditFields,
        canEditContent,
        editingIds,
        renderSection,
        currentWidths,
        containerWidth,
        containerTop,
      },
      tableBodyRef
    ) {
      const scope = useScope();
      const { view } = useView();
      const allFieldsHidden = areAllFieldsHidden(view.settings);

      const [entryIds, fieldCount, isFilterOrSortingActive] =
        defaultStoreUtils.useEqualSelector((state: DefaultRootState) => {
          const database = selectDatabaseDraftByContentId(state, {
            contentId,
            scope,
            viewSettings: view.settings,
            includeDeleted: true,
          });
          return [
            database.entries,
            database.fields.length,
            view.settings?.filter || view.settings?.sort,
          ] as const;
        });

      const [dragOver, setDragOver] = useState<DragOverItem>();
      /** The IDs of the rows to show. A row can be an entry row, the "empty state" row or the "add entry" row. */
      const rowIds = useMemo(() => {
        const result = [...(entryIds.length === 0 ? [EMPTY_ITEM] : entryIds)];

        if (dragOver && !isFilterOrSortingActive) {
          result.splice(dragOver.idx, 0, DRAG_ITEM);
        }

        if (canEditContent) {
          result.push(ADD_ITEM);
        }

        return result;
      }, [entryIds, dragOver, isFilterOrSortingActive, canEditContent]);

      const rowRefMap = useRefMapWithSizes({
        getPositions: ({ sizes }) => {
          const rowPositions: Record<string, VerticalPositions> = {};
          let currentTop = 0;
          for (const entryId of rowIds) {
            const top = currentTop;
            const height =
              entryId === DRAG_ITEM ? 0 : sizes[entryId]?.height ?? FALLBACK_ROW_HEIGHT;
            rowPositions[entryId] = { top, height };
            currentTop += height;
          }

          return rowPositions;
        },
      });

      const client = useAnalytics();
      const store = defaultStoreUtils.useStore();
      const dispatch = defaultStoreUtils.useDispatch();
      const [draggingItem, setDraggingItem] = useState<DraggingIem>();
      const onDrop = useFlaggingCallback(
        ({ idx, ids }) => {
          if (isFilterOrSortingActive) {
            // Prevent dropping when filter or sorting is active
            return;
          }
          batch(() => {
            collabDatabaseBatch(store, contentId, () => {
              void dispatch(
                addChangeToDraft(contentId, scope, {
                  type: DatabaseChangeType.REORDER_ENTRIES,
                  entryIds: ids,
                  newIndex: idx,
                })
              );
            });
          });
          void client.sendRowReorderedEvent({
            contentId: contentId,
            databaseContainer: getDatabaseContainerType(),
          });
        },
        [client, contentId, dispatch, isFilterOrSortingActive, scope, store]
      );

      const topPadding = useMemo(
        () => CONTAINER_PADDING_TOP - (containerTop > 0 ? containerTop + 60 : 0), // remove some padding if the scrollable container is not at the top to be able to insert rows at the last position
        [containerTop]
      );

      const {
        handleDragLeave,
        handleDragEnter,
        handleDragEnd,
        handleDrag,
        handleDrop,
        handleDragOver,
      } = useDragItems({
        dragDirection: 'vertical',
        ids: entryIds,
        rowOrColIds: rowIds,
        contentId,
        positions: rowRefMap.positions,
        dragType: DragType.ENTRY_ROWS,
        onDrop,
        onDragOver: setDragOver,
        onSetDraggingItem: setDraggingItem,
        topOrLeftPadding: topPadding,
      });

      const prevIsDragOver = usePrevious(!!dragOver);
      const renderRowIds = useAnimateItems({
        itemIds: rowIds,
        itemRefMap: rowRefMap,
        renderSection,
        transitionTime: dragOver || prevIsDragOver ? '0s' : undefined,
      });

      /**
       * The rows that should actually be rendered. These include visible rows, rows that need to be animated, a row
       * that is currently being dragged, and gap rows.
       */
      const rowsToRender = useMemo(() => {
        let gapHeight = 0;
        const rows: Array<
          { type: 'gap'; height: number } | { type: 'row'; rowId: string }
        > = [];
        for (const rowId of rowIds) {
          if (renderRowIds.includes(rowId)) {
            if (gapHeight > 0) {
              rows.push({ type: 'gap', height: gapHeight });
              gapHeight = 0;
            }

            rows.push({ type: 'row', rowId });
          } else {
            gapHeight += rowRefMap.positions[rowId].height;
          }
        }
        if (gapHeight > 0) {
          rows.push({ type: 'gap', height: gapHeight });
        }
        return rows;
      }, [rowIds, renderRowIds, rowRefMap.positions]);

      const columnWidths = useMemo(() => {
        const totalColumnWidths = sum(currentWidths.filter((width) => width != null));
        const numberOfNullColumns = currentWidths.filter((width) => width == null).length;
        const flexWidth = Math.max(
          TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
          (containerWidth - totalColumnWidths) / numberOfNullColumns
        );
        return currentWidths.map((width) => (width != null ? width : flexWidth));
      }, [containerWidth, currentWidths]);

      const skeletonSvg = useMemo(
        () => svgToMiniDataURI(getSkeletonRowSvg(columnWidths)),
        [columnWidths]
      );

      const handleSelectCells = useFlaggingCallback(
        (entryId: string) =>
          dispatch(
            addEntryToValueRange({
              contentId,
              entryId,
            })
          ),
        [contentId, dispatch]
      );

      /**
       * Fill dragged values from previously selected cells.
       **/
      useEffect(
        () =>
          monitorForElements({
            canMonitor: ({ source }) => source.data.type === DragType.FILL_VALUES,
            onDrop: ({
              source,
              location,
            }: {
              source: ElementDragPayload;
              location: DragLocationHistory;
            }) => {
              const selectedFieldIds = selectSelectedFieldIds(store.getState(), {
                contentId,
              });
              const selectedEntryIds = selectSelectedEntryIds(store.getState(), {
                contentId,
              });
              const destination = location.current.dropTargets[0];
              if (!destination) {
                return;
              }
              const sourceFieldIds = source.data.selectedFieldIds as string[];
              const sourceEntryIds = source.data.selectedEntryIds as string[];
              dispatch(
                doFillValues(
                  contentId,
                  'main',
                  selectedFieldIds,
                  selectedEntryIds,
                  sourceEntryIds,
                  sourceFieldIds
                )
              );
            },
          }),
        [contentId, dispatch, store]
      );

      /**
       * Create a new entry if the user hover over the "add entry" row during dragging to fill values.
       **/
      useEffect(() => {
        if (!addEntryButtonRef?.current) {
          return;
        }
        return dropTargetForElements({
          element: addEntryButtonRef.current,
          canDrop: ({ source }) => source.data.type === DragType.FILL_VALUES,
          onDragEnter: () => {
            const addedEntry = dispatch(doAddEntry(contentId, scope, view.settings));
            handleSelectCells(addedEntry.id);
          },
        });
      }, [contentId, dispatch, addEntryButtonRef, scope, view, handleSelectCells]);

      /** To allow drag and drop outside of table border */
      useEffect(() => {
        const body = document?.querySelector('body');

        if (body) {
          body.addEventListener('dragover', handleDragOver);
          body.addEventListener('drop', handleDrop);
          body.addEventListener('dragleave', handleDragLeave);
          body.addEventListener('dragenter', handleDragEnter);
        }
        return () => {
          if (body) {
            body.removeEventListener('dragover', handleDragOver);
            body.removeEventListener('drop', handleDrop);
            body.removeEventListener('dragleave', handleDragLeave);
            body.removeEventListener('dragenter', handleDragEnter);
          }
        };
      }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

      return (
        <>
          <tbody ref={tableBodyRef}>
            {allFieldsHidden ? (
              <TableLayoutEmptyStateRow
                contentId={contentId}
                allFieldsHidden={allFieldsHidden}
                canEditContent={canEditContent}
              />
            ) : (
              rowsToRender.map((row, i) =>
                row.type === GAP_ITEM ? (
                  <tr
                    key={`${GAP_ITEM}-${i}`}
                    style={{
                      background: 'transparent',
                      left: HALF_ROW_SELECT_BUTTON_WIDTH,
                    }}
                  >
                    <td
                      colSpan={fieldCount + 1}
                      style={{
                        marginLeft: `${HALF_ROW_SELECT_BUTTON_WIDTH}px`,
                        gridColumn: '1 / -1',
                        height: row.height,
                        background: `url(${encodeCssUrl(skeletonSvg)}) repeat-y`,
                        left: HALF_ROW_SELECT_BUTTON_WIDTH,
                      }}
                    />
                  </tr>
                ) : row.rowId === ADD_ITEM ? null : row.rowId === EMPTY_ITEM ? (
                  <TableLayoutEmptyStateRow
                    key={row.rowId}
                    contentId={contentId}
                    ref={rowRefMap.refs[EMPTY_ITEM]}
                    canEditContent={canEditContent}
                  />
                ) : row.rowId === DRAG_ITEM && dragOver && draggingItem ? (
                  <DropIndicator key={`${DRAG_ITEM}-${i}`} />
                ) : row.rowId === DRAG_ITEM ? null : (
                  <TableLayoutRow
                    key={row.rowId}
                    ref={rowRefMap.refs[row.rowId]}
                    contentId={contentId}
                    entryId={row.rowId}
                    isContentEditable={isContentEditable}
                    canEditFields={canEditFields}
                    canEditContent={canEditContent}
                    editingIds={editingIds}
                    isDraggingActive={!!draggingItem}
                    isLastInColumn={entryIds[entryIds.length - 1] === row.rowId}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    containerWidth={containerWidth}
                    currentViewId={view.id}
                    index={entryIds.indexOf(row.rowId)}
                  />
                )
              )
            )}
          </tbody>
        </>
      );
    }
  )
);

export default TableLayoutRows;
